/**
 * An interface for individual report types (Insights, Funnels, etc) to
 * implement for managing their standard building blocks (e.g., hydrating
 * a query builder from a bookmark, rendering a chart from API results, etc.).
 */
import {Location} from 'react-router';

import {SegmentFilterController, ToggleCallbackArgs} from 'common/charts/contexts/segment-filter-controller';
import {JsxFactory} from 'common/util/jsx';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {AdditionalContext, QueryContext, SegmentationMetadata} from 'common/query-context';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {CurrentProject} from 'common/report/model-resources/projects';
import {UpsellModalAttrs, UpsellModalProps} from 'common/widgets/upsell-modal';
import {QueryContextFnParams} from 'common/report/report-manager/types';
import {DataGroup} from 'common/widgets/data-group-selector/types';

import {BookmarkType} from '../queries/app-api/bookmark-client';
import {ReportTracker, TrackingOptions} from '../report-tracker';
import {BaseQueryErrorType, BaseQueryResultsType, ParamsAndResults} from '../types';
import {ProjectDetails} from '../stores/project-details';
import {CustomEventDefinition} from '../util/saved-entities/types';
import {DashboardOrigin} from '../util/bookmark-overrides';
import {ClientQueryTrackingProps} from '../tracking-setup';
import {DisplayBookmark} from '../dashboards/types';
import {ChartType, ReportName} from '../constants';
import type {QueryManager} from './query';
import {ReportQueryBuilderParams} from './query-builder';
import {ReportVisualizationAttrs, ReportVisualizationProps} from './visualization';
import {ReportHeaderJsxProps} from './report-header';
import {ReportTrackingEvent} from './tracking';

type ReportQueryBuilderJsxProps<
  BookmarkParamsType,
  QueryResultsType extends BaseQueryResultsType = BaseQueryResultsType,
  ErrorType = BaseQueryErrorType
> = {
  params: ReportQueryBuilderParams<BookmarkParamsType, QueryResultsType, ErrorType>;
};

type ReportVisualizationJsxProps<
  BookmarkParamsType,
  QueryResultsType extends BaseQueryResultsType = BaseQueryResultsType,
  QueryErrorType extends BaseQueryErrorType = BaseQueryErrorType
> = {
  attrs: ReportVisualizationAttrs;
  props: ReportVisualizationProps<BookmarkParamsType, QueryResultsType, QueryErrorType>;
};

// constructor should be the same for all report manager sublcasses
export type ReportManagerParams = {
  projectId: number;
  workspaceId?: number;
  location: Location;
  insightsReportMode?: ReportName;
};

// add type defaults so that we don't have to wire generics through places where they're not useful.
export abstract class ReportManager<
  BookmarkParamsType = any,
  QueryResultsType extends BaseQueryResultsType = BaseQueryResultsType,
  QueryErrorType extends BaseQueryErrorType = BaseQueryErrorType
> {
  abstract bookmarkType: BookmarkType;
  abstract reportDisplayName: string;
  abstract tracker: ReportTracker;

  public isCsvExportEnabled = false;
  public projectId: number;
  public workspaceId: number;
  public location: Location;
  public insightsReportMode: ReportName;

  public queryBuilderEl: JsxFactory<ReportQueryBuilderJsxProps<BookmarkParamsType>>;
  public reportHeaderEl: JsxFactory<ReportHeaderJsxProps>;
  public visualizationEl: JsxFactory<ReportVisualizationJsxProps<BookmarkParamsType, QueryResultsType, QueryErrorType>>;

  public queryManager: QueryManager<BookmarkParamsType, QueryResultsType, QueryErrorType>;

  constructor({projectId, workspaceId, location, insightsReportMode}: ReportManagerParams) {
    this.projectId = projectId;
    this.workspaceId = workspaceId;
    this.location = location;
    this.insightsReportMode = insightsReportMode;
  }

  async csvExportQuery({
    bookmark,
    currentProject,
    useQueryCache = true,
    location,
  }: {
    bookmark: DisplayBookmark;
    currentProject: CurrentProject;
    useQueryCache?: boolean;
    location: Location;
  }) {
    const {result} = await this.queryManager.csvExportQuery({
      bookmarkParams: bookmark.params,
      currentProject,
      useQueryCache,
      location,
    });
    const title = bookmark.name || `Untitled Report`;
    const filename = `${title}.csv`;
    return {
      result,
      filename,
    };
  }

  query({
    bookmarkParams,
    currentProject,
    queryReason,
    bookmarkId,
    hasUnsavedBookmarkChanges,
    useQuerySampling,
    useQueryCache = true,
  }: {
    bookmarkParams: BookmarkParamsType;
    currentProject: CurrentProject;
    queryReason: QueryReason;
    bookmarkId: null | number;
    hasUnsavedBookmarkChanges: boolean;
    useQuerySampling: boolean;
    useQueryCache?: boolean;
  }): Promise<QueryResultsType | QueryErrorType> {
    return this.queryManager.query({
      bookmarkParams,
      currentProject,
      queryReason,
      bookmarkId,
      hasUnsavedBookmarkChanges,
      useQuerySampling,
      useQueryCache,
      location: this.location,
    });
  }

  abortPendingQuery() {
    this.queryManager.abortPendingQuery();
  }

  handleResults(
    paramsAndResults: ParamsAndResults<BookmarkParamsType, QueryResultsType>,
  ): ParamsAndResults<BookmarkParamsType, QueryResultsType> {
    return this.queryManager.handleResults(paramsAndResults);
  }

  /**
   * Optional method to convert segmentation data from a query response into a standardized format
   */
  getSegmentationMetadatas(_result: QueryResultsType): SegmentationMetadata[] {
    return null;
  }

  /**
   * Optional method to overwrite describing what kind of analysis the chart is doing.
   * e.g. "Time to Convert", "Logarithmic"
   */
  getAnalysisDescription(_params: BookmarkParamsType): string {
    return null;
  }

  track(event: string, props: Obj = {}, trackingOptions?: TrackingOptions) {
    this.tracker.track(event, props, trackingOptions ?? {});
  }

  /**
   * Optional method to override if the report supports data tables.
   * Use bookmark params to determine if the viz type supports data tables.
   */
  hasDataTable(_params: BookmarkParamsType): boolean {
    return false;
  }

  /**
   * The report editor needs to provide segmentFilterController so that it can set sorting mode based on query changes.
   * this function will be called in segmentFilterController.subscribeToggle and should return a bookmark with any persisted changes.
   * @param segmentFilterController the context provided by ReportEditor
   * @param params current bookmark params
   * @param currentProject used for tracking in insights
   * @param toggleCallbackArgs currently just used for tracking
   */
  public handleSegmentFilterControllerChange(
    _segmentFilterController: SegmentFilterController,
    params: BookmarkParamsType,
    _currentProject?: CurrentProject,
    _toggleCallbackArgs?: ToggleCallbackArgs,
  ): BookmarkParamsType {
    return params;
  }

  /**
   * Called when bookmark is modified.
   * When returning true, sets segmentFilterController.selectMode = SelectMode.Position.
   * This is to default to selecting top n segments based on current sort when segments change.
   */
  public shouldResetSegmentFilterSelectMode(_original: BookmarkParamsType, _edited: BookmarkParamsType): boolean {
    return false;
  }

  /**
   * Optional method to render an upsell if a report is restricted. This is just for flows, and only applies to some legacy plans.
   * TODO: Remove this when flows is available for all plans.
   */
  public getReportUpsell(_project: CurrentProject): {attrs: UpsellModalAttrs; props: UpsellModalProps} | null {
    return null;
  }

  public trackQuery({
    eventName,
    params,
    project,
    extraProps,
    error,
  }: {
    eventName: ReportTrackingEvent;
    params: BookmarkParamsType;
    project: CurrentProject;
    extraProps: Record<string, any>;
    error?: QueryErrorType;
  }) {
    this.tracker.track(eventName, {...this.getClientQueryTrackingProps(params, project, error), ...extraProps});
  }

  abstract getClientQueryTrackingProps(
    _params: BookmarkParamsType,
    _project: CurrentProject,
    _error?: QueryErrorType,
  ): ClientQueryTrackingProps;

  abstract hasUnsavedBookmarkParams(original: BookmarkParamsType, edited: BookmarkParamsType): boolean;

  abstract getDefaultBookmarkParams(project: CurrentProject | ProjectDetails): BookmarkParamsType;

  /**
   * Determine whether results have data to display
   */
  abstract isEmptyResults(results: QueryResultsType): boolean;

  /**
   * Determine whether bookmark params are fully formed enough to make a query
   */
  abstract isQueryable(params: BookmarkParamsType): boolean;

  /**
   * All reports have query types where a date range is specified. If a query type doesn't have a date range, return null.
   */
  abstract getDateRange(params: BookmarkParamsType): DateRange | null;

  abstract getBookmarkParamsFromQueryContext(context: QueryContext, params: QueryContextFnParams): BookmarkParamsType;

  abstract getQueryContextFromBookmarkParams({
    params,
    additionalContext,
    originDashboard,
    querySamplingEnabled,
    project,
    customEvents,
    dataGroups,
  }: {
    params: BookmarkParamsType;
    additionalContext: AdditionalContext;
    originDashboard: DashboardOrigin;
    querySamplingEnabled: boolean;
    project: CurrentProject | ProjectDetails;
    customEvents: CustomEventDefinition[];
    dataGroups: DataGroup[];
  }): QueryContext;

  /**
   * Unfortunately, different report API's respond with different keys for query sampling factor.
   */
  abstract getQuerySamplingFactor(results: QueryResultsType): number;

  /** performs any cleaning/migrations on bookmark params */
  public processBookmarkParams({
    bookmarkParams,
    currentProject: _currentProject,
  }: {
    bookmarkParams: BookmarkParamsType;
    currentProject: CurrentProject;
  }): BookmarkParamsType {
    return bookmarkParams;
  }

  public getCommonChartType(_params: BookmarkParamsType): ChartType {
    return null;
  }

  /** allows certain chart types to route to alternate report managers */
  public reportSwitchInfoForChartType(
    _report: ChartType,
  ): {report: BookmarkType; additionalContext: AdditionalContext} {
    return null;
  }
}
